import { ref, computed, watch, readonly, type ComputedRef, type DeepReadonly, type Ref } from 'vue'
import { useHead } from '@unhead/vue'

import { useCore, getCoreStoreState } from '@/composables/core'

import { PAGE_TITLE, DEFAULT_PAGE_DESCRIPTION } from './consts'
import { generateHrefLangsForCurrentRoute } from './utils'

type HrefLangLink = {
  rel: 'alternate'
  hreflang: string
  href: string
}

type UseHeaderResult = {
  initialize: () => void
  pageTitle: ComputedRef<string>
  pageDescription: ComputedRef<string>
  hrefLangs: DeepReadonly<Ref<HrefLangLink[]>>
}

let head: ReturnType<typeof useHead> | null = null

export function useHeader (): UseHeaderResult {
  const { store, t } = useCore()
  const currentRoute = computed(() => getCoreStoreState(store).app?.currentRoute)

  const hrefLangs = ref<HrefLangLink[]>([])

  const pageTitle = computed(() => {
    return currentRoute.value?.meta?.pageTitle
      ? `${PAGE_TITLE} - ${t(String(currentRoute.value?.meta?.pageTitle ?? ''))}`
      : PAGE_TITLE
  })

  const pageDescription = computed(() => {
    return currentRoute.value?.meta?.description
      ? t(String(currentRoute.value?.meta?.description ?? ''))
      : DEFAULT_PAGE_DESCRIPTION
  })

  const initialize = (): void => {
    if (head) return

    useHead({
      title: pageTitle,
      meta: [
        {
          name: 'description',
          content: pageDescription,
        },
      ],
      link: hrefLangs,
    })

    watch(currentRoute, (newVal, oldVal) => {
      if (newVal?.path !== oldVal?.path) {
        hrefLangs.value = newVal?.meta?.generateHrefLangs
          ? generateHrefLangsForCurrentRoute()
          : []
      }
    }, { immediate: true })
  }

  return {
    initialize,
    pageTitle,
    pageDescription,
    hrefLangs: readonly(hrefLangs),
  }
}
